import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, from, of, switchMap } from 'rxjs';
import { environment } from '../environments/environment';
import { DpopService } from '../service/dpop.service';

// Same backends the auth-refresh interceptor guards: the APIs that validate the
// main JWT. The automation backend (separate token) and blob storage (SAS
// authenticated) are intentionally NOT covered.
const COVERED_BASES = [
  environment.apiUrl,
  (environment as any).scopingApiUrl,
  (environment as any).labourDocUrl,
].filter((b: any): b is string => Boolean(b));

/**
 * DPoP (RFC 9449) — attaches a fresh, signed proof header to every API request
 * so the server can reject replays (jti nonce), stolen tokens (key binding) and
 * cross-endpoint reuse (htm/htu). See DpopService for the crypto details.
 *
 * MUST be registered AFTER authRefreshInterceptor in app.config.ts. Interceptors
 * closer to the backend see the FINAL request: when authRefresh retries a 401
 * with a freshly refreshed token, the retry re-enters the chain here and gets a
 * new proof with a new jti and the new token's ath — reusing the old proof would
 * (correctly) be rejected as a replay.
 *
 * Fail-open by design: if WebCrypto is unavailable or proof creation throws, the
 * request is sent WITHOUT a proof. Such a client also never obtained a key-bound
 * token at login, so the server does not expect a proof from it — behaviour is
 * exactly as before DPoP existed.
 */
export const dpopInterceptor: HttpInterceptorFn = (req, next) => {
  if (!COVERED_BASES.some((base) => req.url.startsWith(base))) {
    return next(req);
  }

  const dpop = inject(DpopService);
  if (!dpop.isSupported) {
    return next(req);
  }

  // Bind the proof to the token actually leaving the app: authRefreshInterceptor
  // has already set the final Authorization header by the time we run.
  const auth = req.headers.get('Authorization') ?? '';
  const accessToken = auth.startsWith('Bearer ') ? auth.substring('Bearer '.length) : undefined;

  return from(dpop.createProof(req.method, req.url, accessToken)).pipe(
    // Only failures of proof CREATION fall back to an unsigned request — HTTP
    // errors from the request itself pass through untouched (the auth-refresh
    // interceptor depends on seeing the 401s).
    catchError(() => of(null)),
    switchMap((proof) =>
      proof ? next(req.clone({ setHeaders: { DPoP: proof } })) : next(req),
    ),
  );
};
